import React from 'react';
import { cn } from '@/lib/utils';

export interface BarDatum {
  label: string;
  value: number;
  tooltip?: string;
}

interface BarChartProps {
  data: BarDatum[];
  unit?: string; // e.g., 'ms'
  max?: number; // optional fixed max; computed from data if omitted
  className?: string;
}

export function HorizontalBarChart({ data, unit, max, className }: BarChartProps) {
  const computedMax = React.useMemo(() => {
    if (typeof max === 'number' && max > 0) return max;
    return data.reduce((m, d) => (d.value > m ? d.value : m), 0) || 1;
  }, [data, max]);

  return (
    <div className={cn('space-y-2', className)}>
      {data.length === 0 ? (
        <div className="text-sm text-muted-foreground">No data</div>
      ) : (
        data.map((d) => <BarRow key={d.label} label={d.label} value={d.value} max={computedMax} unit={unit} tooltip={d.tooltip} />)
      )}
    </div>
  );
}

function BarRow({ label, value, max, unit, tooltip }: { label: string; value: number; max: number; unit?: string; tooltip?: string }) {
  const pct = Math.max(2, Math.round((value / max) * 100));
  const aria = `${label}: ${Math.round(value)}${unit ? ' ' + unit : ''}`;
  return (
    <div className="flex items-center gap-3">
      <div className="w-48 shrink-0 text-xs text-foreground truncate" aria-hidden title={label}>
        {label}
      </div>
      <div className="flex-1 h-4 bg-muted rounded" role="meter" aria-valuenow={Math.round(value)} aria-valuemin={0} aria-valuemax={Math.round(max)} aria-label={aria}>
        <div className="h-4 bg-primary-500 rounded" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-16 text-right text-xs text-muted-foreground" aria-hidden>
        {Math.round(value)}{unit ? ` ${unit}` : ''}
      </div>
    </div>
  );
}


